import { Transform } from "./Transform";
import { Angles } from "./Angles";
export var Vector2;
(function (Vector2) {
    Vector2.ZERO = { x: 0, y: 0 };
    function add(...a) {
        return {
            x: a.map(v => v.x).reduce((c, d) => c + d, 0),
            y: a.map(v => v.y).reduce((c, d) => c + d, 0),
        };
    }
    Vector2.add = add;
    function sub(a, b) {
        return {
            x: a.x - b.x,
            y: a.y - b.y
        };
    }
    Vector2.sub = sub;
    function mul(a, b) {
        return {
            x: a.x * b.x,
            y: a.y * b.y
        };
    }
    Vector2.mul = mul;
    function dot(a, b) {
        return a.x * b.x + a.y * b.y;
    }
    Vector2.dot = dot;
    function det(a, b) {
        return a.x * b.y - a.y * b.x;
    }
    Vector2.det = det;
    function neg(a) {
        return {
            x: -a.x,
            y: -a.y
        };
    }
    Vector2.neg = neg;
    function scale(f, v) {
        return {
            x: v.x * f,
            y: v.y * f
        };
    }
    Vector2.scale = scale;
    function length(a) {
        return Math.sqrt(lengthSquared(a));
    }
    Vector2.length = length;
    function lengthSquared(a) {
        return a.x * a.x + a.y * a.y;
    }
    Vector2.lengthSquared = lengthSquared;
    function normalize(a) {
        return scale(1 / length(a), a);
    }
    Vector2.normalize = normalize;
    function sign(a) {
        return {
            x: Math.sign(a.x),
            y: Math.sign(a.y)
        };
    }
    Vector2.sign = sign;
    function rotate(v, angle_radians) {
        let sin = Math.sin(angle_radians);
        let cos = Math.cos(angle_radians);
        return {
            x: cos * v.x - sin * v.y,
            y: sin * v.x + cos * v.y,
        };
    }
    Vector2.rotate = rotate;
    /**
     * Gets the angle between two normalized vectors
     */
    function angle(a, b) {
        return Angles.normalizeAngle(Math.atan2(Vector2.det(a, b), Vector2.dot(a, b)));
    }
    Vector2.angle = angle;
    function eq(a, b) {
        return a.x == b.x && a.y == b.y;
    }
    Vector2.eq = eq;
    function manhatten(a) {
        return Math.abs(a.x) + Math.abs(a.y);
    }
    Vector2.manhatten = manhatten;
    function max_axis(a) {
        return Math.max(Math.abs(a.x), Math.abs(a.y));
    }
    Vector2.max_axis = max_axis;
    function min_axis(a) {
        return Math.min(Math.abs(a.x), Math.abs(a.y));
    }
    Vector2.min_axis = min_axis;
    function asTuple(point) {
        return [point.x, point.y];
    }
    Vector2.asTuple = asTuple;
    function copy(c) {
        return {
            x: c.x,
            y: c.y
        };
    }
    Vector2.copy = copy;
    function snap(c, grid = 1) {
        return { x: Math.round(c.x / grid) * grid, y: Math.round(c.y / grid) * grid };
    }
    Vector2.snap = snap;
    function swap(c) {
        return { x: c.y, y: c.x };
    }
    Vector2.swap = swap;
    /**
     * Transforms a Vector2 by the given transform, interpreting the Vector as a direction.
     */
    function transform(a, trans) {
        let r = Transform.apply(trans, [a.x, a.y, 0]);
        return { x: r[0], y: r[1] };
    }
    Vector2.transform = transform;
    /**
     * Transforms a Vector2 by the given transform, interpreting the Vector as a point in space.
     */
    function transform_point(a, trans) {
        let r = Transform.apply(trans, [a.x, a.y, 1]);
        return snap({ x: r[0], y: r[1] }, 0.5);
    }
    Vector2.transform_point = transform_point;
    function abs(a) {
        return { x: Math.abs(a.x), y: Math.abs(a.y) };
    }
    Vector2.abs = abs;
    function toString(a) {
        return `${a.x} | ${a.y}`;
    }
    Vector2.toString = toString;
    function hash(c, mod = 64) {
        return Math.floor(Math.abs((c.x ^ c.y) % mod));
    }
    Vector2.hash = hash;
    /**
     * Mirrors a point through a given mirror_point.
     *
     * @param v The point to mirror
     * @param mirror_point The center point
     */
    function mirrorThroughPoint(v, mirror_point) {
        return Vector2.add(mirror_point, sub(mirror_point, v));
    }
    Vector2.mirrorThroughPoint = mirrorThroughPoint;
})(Vector2 || (Vector2 = {}));
